import { FunctionComponent, useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grow,
    styled,
    Typography,
} from '@mui/material';
import { ProductConfiguration } from '@/models/product';
import { FlexContainer } from '@/components/Layout/Container';
import { BookingOptions } from '@/components/BookingOptions/BookingOptions';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import { LoadingButton } from '@mui/lab';
import {
    BulkUpdateActionPayload,
    BulkUpdateType,
    ConfigurationCollection,
    ConfigurationState,
    RemoveConditionConfiguration,
    SourceConfiguration,
} from '@/models/bulkUpdate';
import { initialBookingOptions } from './ConfigurationsPanelContainer';
import { Select } from '@/components/Select/Select';
import { useConfigurationPanelContext } from '@/pageContents/productConfiguration/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { SIDE_NAV_WIDTH } from '@starjack/layout';

const PanelRow = styled(FlexContainer)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    alignItems: 'center',
}));

type ConfigurationsPanelProps = {
    numOfConfigurations: number;
    isActive: boolean;
    removeConditions: RemoveConditionConfiguration;
    sourceConfiguration: SourceConfiguration;
    configurationsWithConditions: ProductConfiguration[];
    configurationCollection: ConfigurationCollection;
    onConfigurationsChange: (action: BulkUpdateActionPayload) => void;
    configurationState: ConfigurationState;
    onConfigurationStateChange: (nextState: ConfigurationState) => void;
    onSave: () => void;
    isLoading: boolean;
};

export const ConfigurationsPanel: FunctionComponent<
    ConfigurationsPanelProps
> = ({
    numOfConfigurations,
    removeConditions,
    isActive,
    sourceConfiguration,
    onConfigurationsChange,
    configurationsWithConditions,
    configurationCollection,
    onSave,
    configurationState,
    onConfigurationStateChange,
    isLoading,
}) => {
    const {
        activeStatus,
        setDynamicPricing,
        setBookingOptions,
        setRemoveConditions,
    } = configurationState;
    const [shrink, setShrink] = useState(false);
    const { setIsCollapsed } = useConfigurationPanelContext();

    useEffect(() => {
        if (numOfConfigurations === 0) {
            setShrink(false);
        }
    }, [numOfConfigurations]);

    useEffect(() => {
        setIsCollapsed(shrink);
    }, [shrink]);

    useEffect(() => {
        if (numOfConfigurations === 0) {
            setShrink(false);
        }
    }, [numOfConfigurations]);

    const onChangeCheckbox = (key: keyof ConfigurationState) => (e) => {
        const { checked } = e.target;

        const nextState = {
            ...configurationState,
            [key]: checked,
        };

        if (key === 'setDynamicPricing') {
            nextState.setRemoveConditions = false;
        } else if (key === 'setRemoveConditions') {
            nextState.setDynamicPricing = false;
        }
        onConfigurationStateChange({
            ...nextState,
        });

        // reset values to null;
        switch (key) {
            case 'activeStatus':
                onConfigurationsChange({
                    type: BulkUpdateType.ACTIVE_STATUS,
                    isActive: null,
                });
                break;
            case 'setDynamicPricing':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_REMOVE_CONDITION,
                    payload: null,
                });
                onConfigurationsChange({
                    type: BulkUpdateType.SET_DYNAMIC_PRICING,
                    sourceConfiguration: {
                        productId: null,
                        copyContingentConditions: false,
                        copyPriceConditions: false,
                    },
                });
                break;

            case 'setRemoveConditions':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_DYNAMIC_PRICING,
                    sourceConfiguration: null,
                });
                onConfigurationsChange({
                    type: BulkUpdateType.SET_REMOVE_CONDITION,
                    payload: {
                        removeContingentConditions: false,
                        removePriceConditions: false,
                    },
                });
                break;
            case 'setBookingOptions':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_BOOKING_OPTIONS,
                    configurations: checked ? initialBookingOptions : null,
                });
                break;
        }
    };

    const containerHeight = setBookingOptions ? 600 : 400;
    return (
        <Grow in={numOfConfigurations > 0}>
            <div
                style={{
                    position: 'fixed',
                    height: containerHeight,
                    left: SIDE_NAV_WIDTH,
                    top: `calc(100vh - ${containerHeight}px)`,
                    bottom: 0,
                    right: 0,
                    zIndex: 10,
                    ...(shrink && {
                        transform: `translateY(${
                            setBookingOptions ? 510 : 320
                        }px)`,
                    }),
                }}
            >
                <Card
                    elevation={3}
                    sx={{
                        height: '100%',
                        width: '100%',
                        overflowY: 'scroll',
                        position: 'relative',
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            height: 60,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            alignItems: 'center',
                        }}
                    >
                        <Typography>
                            {numOfConfigurations}{' '}
                            {numOfConfigurations === 1
                                ? 'Konfiguration'
                                : 'Konfigurationen'}{' '}
                            gewählt
                        </Typography>

                        <IconButton
                            sx={(theme) => ({
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                ml: 'auto',
                                transform: shrink
                                    ? 'rotateZ(180deg)'
                                    : 'rotateZ(0deg)',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            })}
                            onClick={() => {
                                setShrink((prevState) => !prevState);
                            }}
                        >
                            <KeyboardArrowUpIcon />
                        </IconButton>
                        {/*<Button*/}
                        {/*    sx={{*/}
                        {/*        ml: 'auto',*/}
                        {/*    }}*/}
                        {/*    variant='outlined'*/}
                        {/*>*/}
                        {/*    Auswahl aufheben*/}
                        {/*</Button>*/}
                    </CardContent>
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <PanelRow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={activeStatus}
                                        onChange={onChangeCheckbox(
                                            'activeStatus'
                                        )}
                                    />
                                }
                                label='Markierte Produkte für Verkauf aktivieren / deaktivieren (alle Buchungskanäle)'
                            />

                            {activeStatus && (
                                <Select
                                    label='Status'
                                    sx={{
                                        width: '300px',
                                    }}
                                    value={
                                        isActive === null
                                            ? 'active'
                                            : isActive
                                              ? 'active'
                                              : 'inActive'
                                    }
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        onConfigurationsChange({
                                            type: BulkUpdateType.ACTIVE_STATUS,
                                            isActive: value === 'active',
                                        });
                                    }}
                                >
                                    <option value='active'>Aktiv</option>
                                    <option value='inActive'>Inaktiv</option>
                                </Select>
                            )}
                        </PanelRow>

                        <PanelRow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setDynamicPricing}
                                        onChange={onChangeCheckbox(
                                            'setDynamicPricing'
                                        )}
                                    />
                                }
                                label='"Dynamic Pricing" Regeln für markierte Produkte übernehmen'
                            />

                            {setDynamicPricing && (
                                <FlexContainer
                                    sx={{
                                        alignItems: 'center',
                                    }}
                                >
                                    <Select
                                        label='Quellkonfiguration'
                                        sx={{
                                            width: '300px',
                                        }}
                                        value={
                                            sourceConfiguration.productId ||
                                            configurationsWithConditions?.[0]
                                                .productId
                                        }
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            onConfigurationsChange({
                                                type: BulkUpdateType.SET_DYNAMIC_PRICING,
                                                sourceConfiguration: {
                                                    productId: value,
                                                    copyPriceConditions: true,
                                                    copyContingentConditions: true,
                                                },
                                            });
                                        }}
                                    >
                                        {configurationsWithConditions.map(
                                            (configuration, index) => (
                                                <option
                                                    key={index}
                                                    value={
                                                        configuration.productId
                                                    }
                                                >
                                                    {
                                                        configuration.product
                                                            .name.de
                                                    }
                                                </option>
                                            )
                                        )}
                                    </Select>

                                    <FlexContainer
                                        sx={{
                                            ml: 4,
                                        }}
                                        column
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        sourceConfiguration.copyPriceConditions
                                                    }
                                                    onChange={(e) => {
                                                        const { checked } =
                                                            e.target;

                                                        onConfigurationsChange({
                                                            type: BulkUpdateType.SET_DYNAMIC_PRICING,
                                                            sourceConfiguration:
                                                                {
                                                                    ...sourceConfiguration,
                                                                    copyPriceConditions:
                                                                        checked,
                                                                },
                                                        });
                                                    }}
                                                />
                                            }
                                            label='Preisregeln übernehmen'
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        sourceConfiguration.copyContingentConditions
                                                    }
                                                    onChange={(e) => {
                                                        const { checked } =
                                                            e.target;

                                                        onConfigurationsChange({
                                                            type: BulkUpdateType.SET_DYNAMIC_PRICING,
                                                            sourceConfiguration:
                                                                {
                                                                    ...sourceConfiguration,
                                                                    copyContingentConditions:
                                                                        checked,
                                                                },
                                                        });
                                                    }}
                                                />
                                            }
                                            label='Kontingentregeln übernehmen'
                                        />
                                    </FlexContainer>
                                </FlexContainer>
                            )}
                        </PanelRow>

                        <PanelRow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setRemoveConditions}
                                        onChange={onChangeCheckbox(
                                            'setRemoveConditions'
                                        )}
                                    />
                                }
                                label='Regelkonfiguration zurücksetzen'
                            />

                            {!!setRemoveConditions && (
                                <FlexContainer
                                    sx={{
                                        ml: 4,
                                    }}
                                    column
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    removeConditions.removePriceConditions
                                                }
                                                onChange={(e) => {
                                                    const { checked } =
                                                        e.target;

                                                    onConfigurationsChange({
                                                        type: BulkUpdateType.SET_REMOVE_CONDITION,
                                                        payload: {
                                                            ...removeConditions,
                                                            removePriceConditions:
                                                                checked,
                                                        },
                                                    });
                                                }}
                                            />
                                        }
                                        label='Preisregeln zurücksetzen'
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    removeConditions.removeContingentConditions
                                                }
                                                onChange={(e) => {
                                                    const { checked } =
                                                        e.target;

                                                    onConfigurationsChange({
                                                        type: BulkUpdateType.SET_REMOVE_CONDITION,
                                                        payload: {
                                                            ...removeConditions,
                                                            removeContingentConditions:
                                                                checked,
                                                        },
                                                    });
                                                }}
                                            />
                                        }
                                        label='Kontingentregeln zurücksetzen '
                                    />
                                </FlexContainer>
                            )}
                        </PanelRow>

                        <PanelRow
                            sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setBookingOptions}
                                        onChange={onChangeCheckbox(
                                            'setBookingOptions'
                                        )}
                                    />
                                }
                                label='Buchungsoptionen für markierte Produkte anpassen'
                            />

                            {setBookingOptions && (
                                <FlexContainer
                                    sx={{
                                        mx: 3,
                                    }}
                                >
                                    <BookingOptions
                                        onChange={(configurationCollection) => {
                                            onConfigurationsChange({
                                                type: BulkUpdateType.SET_BOOKING_OPTIONS,
                                                configurations:
                                                    configurationCollection,
                                            });
                                        }}
                                        configurations={configurationCollection}
                                    />
                                </FlexContainer>
                            )}
                        </PanelRow>
                    </CardContent>

                    <LoadingButton
                        sx={{
                            position: 'absolute',
                            right: '24px',
                            bottom: '24px',
                        }}
                        onClick={() => {
                            onSave();
                        }}
                        variant='contained'
                        loading={isLoading}
                    >
                        Auswahl speichern
                    </LoadingButton>
                </Card>
            </div>
        </Grow>
    );
};
